import { http } from "./client";
import { fixMojibake } from "../utils/text";

// Employee profile sections: personal details, addresses, contacts,
// employment history, education and government documents.
// Each function returns the same data shape the old Redux thunks returned.
const data = (r) => r.data;
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

/* ---------- Personal details ---------- */
export const personalDetailsApi = {
  getByEmployee: (employeeId) =>
    http.get(`/employee/personal-details/${employeeId}`).then(data),
  create: (payload) =>
    http
      .post("/employee/personal-details", {
        ...payload,
        gender: payload.gender || null, // nullable; "" is a 400
        civilStatus: payload.civilStatus || "Single",
        religion: payload.religion || "Prefer not to say",
      })
      .then(data),
  update: (id, payload) =>
    http
      .put(`/employee/personal-details/${id}`, {
        ...payload,
        gender: payload.gender || null, // nullable; "" is a 400
        civilStatus: payload.civilStatus || "Single",
        religion: payload.religion || "Prefer not to say",
      })
      .then(data),
};

/* ---------- Addresses ---------- */
// fixMojibake: older records may have been saved as "Las PiÃ±as" (see utils/text.js)
const normalizeAddress = (addr) => ({
  id: addr.id,
  employeeId: addr.employee?.id || null,
  addressType: addr.addressType || "",
  addressNum: fixMojibake(addr.addressNum || ""),
  addressLine1: fixMojibake(addr.addressLine1 || ""),
  addressLine2: fixMojibake(addr.addressLine2 || ""),
  city: fixMojibake(addr.city || ""),
  municipality: fixMojibake(addr.municipality || ""),
  province: fixMojibake(addr.province || ""),
  region: fixMojibake(addr.region || ""),
  postalCode: addr.postalCode || "",
  country: addr.country || "Philippines",
});

export const addressApi = {
  getCurrent: (employeeId) =>
    http
      .get(`/employee/address/employee/${employeeId}/current`)
      .then((r) => normalizeAddress(r.data)),
  getPermanent: (employeeId) =>
    http
      .get(`/employee/address/employee/${employeeId}/permanent`)
      .then((r) => normalizeAddress(r.data)),
  copyCurrentToPermanent: (employeeId) =>
    http
      .post(
        `/employee/address/employee/${employeeId}/copy-current-to-permanent`,
      )
      .then((r) => normalizeAddress(r.data)),
  create: (payload) =>
    http
      .post("/employee/address", payload)
      .then((r) => normalizeAddress(r.data)),
  update: (id, payload) =>
    http
      .put(`/employee/address/${id}`, payload)
      .then((r) => normalizeAddress(r.data)),
};

/* ---------- Emergency / employee contacts ---------- */
const toContactPayload = (employeeId, c) => ({
  employeeId,
  firstName: c.firstName,
  middleName: c.middleName,
  lastName: c.lastName,
  emergencyContact: c.emergencyContact || false,
  relationship: c.relationship,
  phone: c.phone,
  email: c.email,
  address: c.address,
});

export const contactApi = {
  getByEmployee: (employeeId) =>
    http
      .get(`/employee/emergency-contact/employee/${employeeId}`)
      .then((r) => (Array.isArray(r.data) ? r.data : r.data ? [r.data] : [])),
  create: (employeeId, contact) =>
    http
      .post(
        "/employee/emergency-contact",
        toContactPayload(employeeId, contact),
      )
      .then(data),
  update: (id, contact) =>
    http
      .put(`/employee/emergency-contact/${id}`, {
        ...toContactPayload(contact.employeeId, contact),
        emergencyContact: contact.emergencyContact,
      })
      .then(data),
  remove: (id) =>
    http.delete(`/employee/emergency-contact/${id}`).then(() => id),
};

/* ---------- Employment history ---------- */
export const employmentHistoryApi = {
  getByEmployee: (employeeId) =>
    http
      .get(`/employee/employment-history/employee/${employeeId}`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),
  create: (payload) =>
    http.post("/employee/employment-history", payload).then(data),
  update: (id, payload) =>
    http.put(`/employee/employment-history/${id}`, payload).then(data),
  remove: (id) =>
    http.delete(`/employee/employment-history/${id}`).then(() => id),
};

/* ---------- Education ---------- */
const normalizeEducation = (edu) => ({
  id: edu.id,
  employeeId: edu.employee?.id || null,
  level: edu.level || "",
  schoolName: edu.schoolName || "",
  schoolAddress: edu.schoolAddress || "",
  yearFrom: edu.yearFrom || null,
  yearTo: edu.yearTo || null,
  course: edu.course || "",
  file_url: edu.file_url || null,
  createdAt: edu.createdAt,
  updatedAt: edu.updatedAt,
});

export const educationApi = {
  getByEmployee: (employeeId) =>
    http
      .get(`/employee/education-background/employee/${employeeId}`)
      .then((r) =>
        Array.isArray(r.data) ? r.data.map(normalizeEducation) : [],
      ),
  create: (payload) =>
    http
      .post("/employee/education-background", payload)
      .then((r) => normalizeEducation(r.data)),
  update: (id, payload) =>
    http
      .put(`/employee/education-background/${id}`, payload)
      .then((r) => normalizeEducation(r.data)),
  uploadFile: (id, file, companyId) => {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post(
        `/employee/education-background/${id}/upload?companyId=${companyId}`,
        formData,
        multipart,
      )
      .then((r) => ({ url: r.data.file_url, id: r.data.id }));
  },
  remove: (id) =>
    http.delete(`/employee/education-background/${id}`).then(() => id),
};

/* ---------- Government documents ---------- */
const toDocument = (d) => ({
  docId: d.id,
  docType: d.doc_type,
  docName: d.docName,
  idNumber: d.id_number,
  isValidated: d.isValidated,
  file: d.file_url,
  customName: d.custom_name,
});

export const governmentDocumentApi = {
  getByEmployee: (employeeId) =>
    http
      .get(`/government-documents/employee/${employeeId}`)
      .then((r) => r.data.map(toDocument)),
  create: (employeeId, payload) =>
    http
      .post(`/government-documents/employee/${employeeId}`, payload)
      .then((r) => toDocument(r.data)),
  update: (id, payload) =>
    http
      .patch(`/government-documents/${id}`, payload)
      .then((r) => toDocument(r.data)),
  uploadFile: (id, file, companyId) => {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post(
        `/government-documents/${id}/upload?companyId=${companyId}`,
        formData,
        multipart,
      )
      .then((r) => ({ url: r.data.file_url, id }));
  },
  remove: (id) => http.delete(`/government-documents/${id}`).then(() => id),
  validate: (docId) =>
    http
      .patch(`/government-documents/${docId}/gov-docs-val`)
      .then((r) => ({ docId, result: r.data })),
};
